// Polyfork low-poly vehicles: loaded after the scene is up, then swapped into the procedural traffic.
// The procedural cars stay as the fallback if a file fails to load.
import * as THREE from 'three';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';
import { pick } from '../lib/random.js';
import { toonify } from '../core/optimise.js';
import { lightRoles, repaint } from '../rules/vehicle-paint.js';
import { carHead, carTail, cars } from './cars.js';

const M_PER_U = 1.9; // world scale: one unit is 1.9 m, and the models are in metres

// which models stand in for each procedural type; `paint` is the model's body colour (linear rgb, read from the
// file), repainted with `color` or else the car's own colour
const SLOTS = {
  hatch: [
    { file: 'compact-hatchback', paint: [0.46, 0.25, 0.05] },
    { file: 'family-crossover-suv', paint: [0.09, 0.12, 0.15] },
  ],
  sedan: [{ file: 'family-minivan', paint: [0.57, 0.39, 0.2] }, { file: 'town-car' }],
  van: [
    { file: 'boxy-delivery-van', paint: [1, 1, 1] },
    { file: 'kei-van', paint: [0.91, 0.92, 0.93] },
    { file: 'delivery-truck' },
  ],
  taxi: [{ file: 'city-taxi' }],
  bus: [{ file: 'city-bus', paint: [0.84, 0.78, 0.65], color: 0xd2302a }], // cream body to TMB red
};
// one of each special vehicle replaces a private car or van at a fixed pool slot
const SPECIAL = { 7: 'garbage-truck', 13: 'ambulance-van' };

// One non-indexed geometry per file, in world units, centred on x/z with the wheels on y = 0, facing +z.
async function loadGeometry(loader, file) {
  const wheelIds = new Map();
  const gltf = await loader.loadAsync(`assets/vehicles/${file}.glb`);
  gltf.scene.updateMatrixWorld(true);
  const parts = [];
  gltf.scene.traverse((o) => {
    if (!o.isMesh) return;
    const c = o.geometry.attributes.color;
    // the helpers read COLOR_0 as plain float rgb; anything else fails the load, so the procedural cars stay
    if (!c || c.itemSize !== 3 || c.normalized)
      throw new Error(`${file}: unsupported vertex colours`);
    const g = (o.geometry.index ? o.geometry.toNonIndexed() : o.geometry.clone()).applyMatrix4(
      o.matrixWorld,
    );
    // wheel parts are tagged 1..n (the `wheel-xx` node above them), everything else 0
    let w = o;
    while (w && !/^wheel-..$/.test(w.name)) w = w.parent;
    const id = w ? (wheelIds.get(w) ?? wheelIds.set(w, wheelIds.size + 1).get(w)) : 0;
    g.setAttribute(
      'hub',
      new THREE.BufferAttribute(new Float32Array(g.attributes.position.count * 4).fill(id), 4),
    );
    parts.push(g);
  });
  const geo = mergeGeometries(parts);
  geo.scale(1 / M_PER_U, 1 / M_PER_U, 1 / M_PER_U);
  geo.computeBoundingBox();
  const b = geo.boundingBox;
  geo.translate(-(b.min.x + b.max.x) / 2, -b.min.y, -(b.min.z + b.max.z) / 2);
  geo.computeBoundingBox();
  // each wheel vertex gets its hub (y, z), the wheel radius and a 1 flag; the rest (0, 0, 1, 0)
  const pos = geo.attributes.position.array,
    hub = geo.attributes.hub.array,
    ext = new Map();
  for (let v = 0; v < hub.length / 4; v++) {
    const id = hub[v * 4];
    if (!id) continue;
    const e = ext.get(id) ?? [Infinity, -Infinity, Infinity, -Infinity];
    e[0] = Math.min(e[0], pos[v * 3 + 1]);
    e[1] = Math.max(e[1], pos[v * 3 + 1]);
    e[2] = Math.min(e[2], pos[v * 3 + 2]);
    e[3] = Math.max(e[3], pos[v * 3 + 2]);
    ext.set(id, e);
  }
  for (let v = 0; v < hub.length / 4; v++) {
    const e = ext.get(hub[v * 4]);
    hub.set(e ? [(e[0] + e[1]) / 2, (e[2] + e[3]) / 2, (e[1] - e[0]) / 2, 1] : [0, 0, 1, 0], v * 4);
  }
  return geo;
}

// Light faces go to their own groups so they share the night glow of the procedural cars' lamps.
function withLights(geo) {
  const pos = geo.attributes.position.array,
    col = geo.attributes.color.array,
    hub = geo.attributes.hub.array,
    roles = lightRoles(
      pos,
      col,
      Uint8Array.from({ length: pos.length / 9 }, (_, t) => hub[t * 12 + 3]),
    ),
    order = [0, 1, 2].flatMap((r) => [...roles.keys()].filter((t) => roles[t] === r));
  const out = new THREE.BufferGeometry();
  for (const name of ['position', 'normal', 'color', 'hub']) {
    const k = geo.attributes[name].itemSize * 3, // floats per triangle
      src = geo.attributes[name].array,
      dst = new Float32Array(src.length);
    order.forEach((t, i) => dst.set(src.subarray(t * k, t * k + k), i * k));
    out.setAttribute(name, new THREE.BufferAttribute(dst, k / 3));
  }
  let start = 0;
  for (let r = 0; r < 3; r++) {
    const n = roles.filter((v) => v === r).length * 3;
    if (n) out.addGroup(start, n, r);
    start += n;
  }
  out.boundingBox = geo.boundingBox.clone();
  out.computeBoundingSphere();
  return out;
}

export async function loadCarModels() {
  const loader = new GLTFLoader(),
    files = [
      ...new Set([
        ...Object.values(SLOTS)
          .flat()
          .map((s) => s.file),
        ...Object.values(SPECIAL),
      ]),
    ],
    geos = Object.fromEntries(
      await Promise.all(files.map(async (f) => [f, withLights(await loadGeometry(loader, f))])),
    );
  // wheels roll in the vertex shader: each wheel vertex turns about its hub by distance travelled / radius
  function roll(sh) {
    sh.uniforms.uRoll = this.userData.roll;
    sh.vertexShader = ('attribute vec4 hub;\nuniform float uRoll;\n' + sh.vertexShader)
      .replace(
        '#include <beginnormal_vertex>',
        `#include <beginnormal_vertex>
  float ra = hub.w * uRoll / hub.z, rc = cos(ra), rs = sin(ra);
  objectNormal.yz = vec2(rc * objectNormal.y - rs * objectNormal.z, rs * objectNormal.y + rc * objectNormal.z);`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
  vec2 rd = transformed.yz - hub.xy;
  transformed.yz = hub.xy + vec2(rc * rd.x - rs * rd.y, rs * rd.x + rc * rd.y);`,
      );
  }
  // one body material per car, so each carries its own roll (three.js uploads uniforms per material);
  // they all compile to one shared program
  const bodyMat = () => {
    const m = new THREE.MeshStandardMaterial({
      vertexColors: true,
      roughness: 0.5,
      metalness: 0.15,
    });
    m.userData.roll = { value: 0 };
    m.onBeforeCompile = roll;
    toonify(m);
    return m;
  };
  const cache = new Map(),
    tmp = new THREE.Color(),
    swaps = [];

  // build every replacement first, then swap, so a failure leaves all the procedural cars in place
  for (const c of cars) {
    if (!c.type) continue; // npc scooters keep their bikes
    const special = !c.taxi && c.type !== 'bus' && SPECIAL[c.pool],
      slot = special ? { file: special } : pick(SLOTS[c.taxi ? 'taxi' : c.type]),
      color = slot.paint && (slot.color ?? c.color),
      key = slot.file + ':' + (color || '');
    if (!cache.has(key)) {
      const g = geos[slot.file].clone();
      if (color) {
        tmp.setHex(color); // linear rgb, like the glTF vertex colours
        g.attributes.color.array.set(
          repaint(g.attributes.color.array, slot.paint, [tmp.r, tmp.g, tmp.b]),
        );
      }
      cache.set(key, g); // lights were split out before the repaint, so they keep their own glow
    }
    const geo = cache.get(key),
      body = bodyMat(),
      mesh = new THREE.Mesh(geo, [body, carHead, carTail]),
      last = new THREE.Vector3().copy(c.g.position);
    mesh.castShadow = mesh.receiveShadow = true;
    // distance rolled since the last frame drawn; a jump (a car re-dealt to its lane) is not rolled
    mesh.onBeforeRender = () => {
      const d = last.distanceTo(c.g.position);
      if (d < 2) body.userData.roll.value += d;
      last.copy(c.g.position);
    };
    swaps.push([c, mesh, geo.boundingBox.getSize(new THREE.Vector3())]);
  }
  for (const [c, mesh, size] of swaps) {
    // drop the procedural parts (their geometry is per car; materials are shared, so they stay)
    for (const o of [...c.g.children]) {
      o.traverse((m) => m.geometry?.dispose());
      c.g.remove(o);
    }
    c.g.add(mesh);
    c.len = size.z;
    c.w = size.x;
  }
}
